import React from "react";
import { useTranslation } from "react-i18next";

import { Button, Modal, Tooltip } from "antd";
import { EyeOutlined, FilePdfOutlined } from "@ant-design/icons";

import { API_URL } from "../../shared/utils/Util";
import { useNotification } from "../../shared/hooks";
import { downloadFile, fetchBlobURL, slugify } from "./download";

type SignPreviewButtonProps = {
  restaurantId: string;
  table_id: string;
  table_name: string;
};

// SignPreviewButton shows the table's print-ready sign in a modal, so the
// user can check it before printing, with a download shortcut in the footer.
export const SignPreviewButton = (props: SignPreviewButtonProps) => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [loading, setLoading] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [previewURL, setPreviewURL] = React.useState<string | undefined>(undefined);

  const signURL = `${API_URL}/v1/r/${props.restaurantId}/tables/${props.table_id}/sign`;

  const onOpen = async () => {
    setLoading(true);
    try {
      setPreviewURL(await fetchBlobURL(signURL, "application/pdf"));
    } catch (err) {
      showError(t("failed_to_download_sign"));
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    if (previewURL !== undefined) {
      window.URL.revokeObjectURL(previewURL);
    }
    setPreviewURL(undefined);
  };

  const onDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile(signURL, `${slugify(props.table_name)}_sign.pdf`, "application/pdf");
    } catch (err) {
      showError(t("failed_to_download_sign"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Tooltip title={t("preview_sign")}>
        <Button shape="circle" icon={<EyeOutlined />} loading={loading} onClick={onOpen} />
      </Tooltip>
      <Modal
        title={props.table_name}
        open={previewURL !== undefined}
        onCancel={onClose}
        width={480}
        footer={[
          <Button
            key="download"
            icon={<FilePdfOutlined />}
            loading={downloading}
            onClick={onDownload}
          >
            {t("download_sign")}
          </Button>,
        ]}
      >
        {previewURL !== undefined && (
          <iframe
            src={`${previewURL}#toolbar=0&navpanes=0`}
            title={t("preview_sign")}
            style={{ width: "100%", height: "62vh", border: "none" }}
          />
        )}
      </Modal>
    </>
  );
};
